import { LegalLayout } from './Layout.tsx';

/**
 * /terms — placeholder Terms of Service. Like Privacy, this is a
 * working draft that will be attorney-reviewed before Phase-1 launch.
 * The plain-English voice is intentional: parents are the audience.
 */
export function TermsPage(): JSX.Element {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="May 5, 2026">
      <p>
        Maysterko is a 3D design teaching tool for kids ages 5–12, built by ThinkAhead Lab. These
        terms cover what we promise and what we ask of the parents who sign up. Plain English; no
        legalese where we can avoid it.
      </p>

      <h2>The service</h2>
      <p>
        Maysterko is a free browser-based product (we may add optional paid features later; any
        change there will be announced separately). It runs on Cloudflare Pages and Render. We do
        our best to keep it up but offer no SLA — if you depend on Maysterko for something critical,
        please save your designs locally as 3MF files.
      </p>

      <h2>Who can use it</h2>
      <p>
        Maysterko is designed for kids 5–12. A parent or legal guardian must create the account and
        provide verifiable consent before a child&rsquo;s designs are stored on our servers. See the{' '}
        <a href="/privacy">Privacy page</a> for the details.
      </p>

      <h2>What you (the parent) agree to</h2>
      <ul>
        <li>You&rsquo;re at least 18 years old.</li>
        <li>You&rsquo;re the parent or legal guardian of the child using the account.</li>
        <li>
          You won&rsquo;t use the service to harass, harm, or impersonate anyone, or to create
          content that&rsquo;s illegal, hateful, or sexually explicit.
        </li>
        <li>
          You&rsquo;ll keep your sign-in credentials private and let us know promptly if you suspect
          they&rsquo;ve been compromised.
        </li>
      </ul>

      <h2>What we agree to</h2>
      <ul>
        <li>We&rsquo;ll handle your child&rsquo;s data the way the Privacy page describes.</li>
        <li>We&rsquo;ll never sell or share your or your child&rsquo;s data for advertising.</li>
        <li>
          We&rsquo;ll let you delete the account at any time and remove all stored data within 7
          days.
        </li>
        <li>
          We&rsquo;ll give you 30 days&rsquo; notice for any material change to these terms or the
          privacy policy.
        </li>
      </ul>

      <h2>Designs your child makes</h2>
      <p>
        Your child owns what they create in Maysterko. We don&rsquo;t claim any rights over their
        designs beyond what&rsquo;s necessary to display, store, and let you export them.
      </p>

      <h2>Things we can&rsquo;t fully promise</h2>
      <p>
        Maysterko is provided &ldquo;as-is.&rdquo; We can&rsquo;t guarantee that every 3D design
        your child makes will print successfully on a home printer; the in-app printability
        validator does its best, but real printers vary. We can&rsquo;t guarantee uninterrupted
        service. To the extent allowed by law, we&rsquo;re not liable for indirect or consequential
        damages from using the service.
      </p>

      <h2>Closing the account</h2>
      <p>
        You can close your account at any time from the parent dashboard (when it ships) or by
        emailing <a href="mailto:info@thinkaheadlab.com">info@thinkaheadlab.com</a>. We can also
        close an account if it&rsquo;s being used in violation of these terms, though we&rsquo;ll
        usually email you first.
      </p>

      <h2>Contact</h2>
      <p>
        Questions: <a href="mailto:info@thinkaheadlab.com">info@thinkaheadlab.com</a>.
      </p>

      <hr />
      <p>
        <em>
          Working draft — will be tightened by our attorney before public launch. If anything here
          seems unfair or unclear, please let us know.
        </em>
      </p>
    </LegalLayout>
  );
}
